// splatSync.ts — ordering rules between the Viewer's two splat sources: the
// HTTP GET /splats (the STORED cloud: seed or last finished refine) and the
// /live socket (SNAP = the hub's newest training cloud, DELT = changed ids
// against it). Kept pure so the races are unit tested headless.
//
// * An HTTP result is dropped when the page moved to another scan, when a
//   live SNAP landed after the request started (the SNAP is newer and later
//   DELTs patch IT — replacing it would leave them patching a stale base), or
//   when a newer HTTP load superseded it.
// * When a followed scan settles (capturing/refining → ready/failed) the
//   socket closes, possibly before a slow client received the final cloud,
//   so the stored splats (written before the status broadcast) are reloaded.

import type { ScanStatus } from '../api';

export interface SplatLoadTicket {
  id: string;
  snapshots: number;
  seq: number;
}

export type SplatLoadVerdict = 'ok' | 'other-scan' | 'live-newer' | 'superseded';

export class SplatLoadGuard {
  private snapshots = 0;
  private seq = 0;

  // begin records the state an HTTP splat load starts from.
  begin(id: string): SplatLoadTicket {
    return { id, snapshots: this.snapshots, seq: ++this.seq };
  }

  // noteSnapshot is called for every live SNAP applied to the page.
  noteSnapshot(): void {
    this.snapshots++;
  }

  // verdict says whether a finished HTTP load may replace the page's cloud.
  verdict(t: SplatLoadTicket, currentId: string): SplatLoadVerdict {
    if (t.id !== currentId) return 'other-scan';
    if (t.seq !== this.seq) return 'superseded';
    if (this.snapshots !== t.snapshots) return 'live-newer';
    return 'ok';
  }
}

const FOLLOWED: ReadonlySet<ScanStatus | undefined> = new Set<ScanStatus | undefined>(['capturing', 'refining']);

// isFollowed reports whether the Viewer keeps /live open for this status.
export function isFollowed(status: ScanStatus | undefined): boolean {
  return FOLLOWED.has(status);
}

// shouldReloadSplats is true when a followed scan settles, so the page swaps
// to the authoritative stored cloud instead of trusting the last frame the
// socket delivered before it closed.
export function shouldReloadSplats(prev: ScanStatus | undefined, now: ScanStatus | undefined): boolean {
  return isFollowed(prev) && (now === 'ready' || now === 'failed');
}
